import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import * as Common from '@lsq-frontend/remote-services/enterprise/common';
import { format, subMonths } from 'date-fns';
import { EarlyPaidAdoption } from '../utils/types';
import { DateFormat } from '@lsq-frontend/common-ui';

export class EarlyPaidAdoptionRequest extends Common.AsOfListResponse<EarlyPaidAdoptionRequest> {
    public buyerKey: string;
    public startDate: string;
    public endDate: string;
    public frecuency: string;

    constructor(initial?: Partial<EarlyPaidAdoptionRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class EarlyPaidAdoptionResponse extends Common.AsOfListResponse<EarlyPaidAdoptionResponse> {
    constructor(initial?: Partial<EarlyPaidAdoptionResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export interface EarlyPaidAdoptionResponse {
    fundingUtilizations: EarlyPaidAdoption[];
}

@Injectable({ providedIn: 'root' })
export class EarlyPaidAdoptionService extends ApiService<
    EarlyPaidAdoptionRequest,
    EarlyPaidAdoptionResponse
> {
    public requestType = EarlyPaidAdoptionRequest;
    public responseType = EarlyPaidAdoptionResponse;
    public endpointName = 'v1/query';
    public serviceName = 'analyticsGql';
    public usePascalForUrl = false;

    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
    }

    public requestMapper(req: EarlyPaidAdoptionRequest): { query: string } {
        const query = this.mapToGraphQLQuery(req);
        return { query: `{${jsonToGraphQLQuery(query)}}` };
    }

    public responseMapper(
        res: GraphQLResponse<EarlyPaidAdoptionResponse>
    ): EarlyPaidAdoptionResponse {
        const data = res as GraphQLResponse<EarlyPaidAdoptionResponse>;
        return new EarlyPaidAdoptionResponse({
            fundingUtilizations: data.data.fundingUtilizations
        });
    }

    private mapToGraphQLQuery(req: EarlyPaidAdoptionRequest): any {
        const startDate = format(subMonths(new Date(), 12), `YYYY-MM`) + '-01';
        const endDate = format(new Date(), DateFormat.ISO8601Numbers);
        return {
            fundingUtilizations: {
                __args: {
                    input: {
                        buyerKeys: [req.buyerKey],
                        frequency: new EnumType('MONTH'),
                        startDate: startDate.toString(),
                        endDate: endDate.toString()
                    }
                },
                date: true,
                openInvoiceAmount: {
                    cents: true,
                    currencyCode: true
                },
                fundedAmount: {
                    cents: true,
                    currencyCode: true
                },
                medianCreditLimit: {
                    cents: true,
                    currencyCode: true
                }
            }
        };
    }

    public get endpointUrl(): string {
        return super.endpointUrl.replace('/api', '');
    }
}
